import { useQuery } from '@tanstack/react-query';
import { safeFormat } from '@/lib/safeDate';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, History, CheckCircle2, XCircle, RefreshCw, User, CreditCard, DollarSign, RotateCcw } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getStripeErrorMessage } from '@/lib/stripeErrorMessages';

interface ChargeHistoryItem {
  id: string;
  payment_id: string;
  amount: number | null;
  currency: string | null;
  status: string;
  transaction_type: string | null;
  trigger_type: 'manual' | 'automatic' | null;
  attempt_number: number | null;
  error_code: string | null;
  notes: string | null;
  payment_method: string | null;
  reference_number: string | null;
  stripe_payment_intent_id: string | null;
  transaction_date: string | null;
  created_at: string | null;
  recorded_by: string | null;
  recorded_by_user?: {
    email: string;
    display_name: string | null;
  } | null;
}

interface ChargeAttemptsHistoryProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  paymentId: string | null;
  paymentDescription?: string;
}

function useChargeHistory(paymentId: string | null) {
  return useQuery({
    queryKey: ['charge-history', paymentId],
    queryFn: async () => {
      if (!paymentId) return [];

      // Fetch from payment_transactions (unified table)
      const { data: transactions, error } = await supabase
        .from('payment_transactions')
        .select('*')
        .eq('payment_id', paymentId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (!transactions) return [];

      // Get unique user IDs that recorded transactions
      const userIds = [...new Set(transactions.filter(t => t.recorded_by).map(t => t.recorded_by))];
      
      // Fetch user info from app_users
      let usersMap: Record<string, { email: string; display_name: string | null }> = {};
      if (userIds.length > 0) {
        const { data: users } = await supabase
          .from('app_users')
          .select('id, email, display_name')
          .in('id', userIds as string[]);
        
        if (users) {
          usersMap = users.reduce((acc, u) => {
            if (u.id) {
              acc[u.id] = { email: u.email || '', display_name: u.display_name };
            }
            return acc;
          }, {} as Record<string, { email: string; display_name: string | null }>);
        }
      }

      // Map transactions with user info
      return transactions.map(tx => ({
        ...tx,
        recorded_by_user: tx.recorded_by ? usersMap[tx.recorded_by] || null : null
      })) as ChargeHistoryItem[];
    },
    enabled: !!paymentId,
  });
}

export function ChargeAttemptsHistory({
  open,
  onOpenChange,
  paymentId,
  paymentDescription
}: ChargeAttemptsHistoryProps) {
  const { data: history, isLoading } = useChargeHistory(paymentId);

  const getTransactionTypeIcon = (type: string | null) => {
    switch (type) {
      case 'charge_attempt':
        return <CreditCard className="h-3 w-3" />;
      case 'refund':
        return <RotateCcw className="h-3 w-3" />;
      default:
        return <DollarSign className="h-3 w-3" />;
    }
  };

  const getTransactionTypeLabel = (type: string | null) => {
    switch (type) {
      case 'charge_attempt':
        return 'Tentativa de pagamento';
      case 'auto_charge':
        return 'Pagamento automático';
      case 'refund':
        return 'Estorno';
      default:
        return 'Pagamento';
    }
  };

  const formatPaymentMethod = (method: string | null) => {
    switch (method) {
      case 'stripe':
        return 'Stripe';
      case 'pix':
        return 'PIX';
      case 'cash':
        return 'Dinheiro';
      case 'bank_transfer':
        return 'Transferência';
      case 'credit_card':
        return 'Cartão';
      case 'check':
        return 'Cheque';
      default:
        return method || '-';
    }
  };

  const isSuccess = (status: string) => {
    return ['confirmed', 'success', 'succeeded'].includes(status);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[1100px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Histórico de Transações
          </DialogTitle>
          {paymentDescription && (
            <DialogDescription>
              {paymentDescription}
            </DialogDescription>
          )}
        </DialogHeader>

        <ScrollArea className="max-h-[400px]">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : !history || history.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhuma transação registrada.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Data Pagamento</TableHead>
                  <TableHead>Método</TableHead>
                  <TableHead>Referência</TableHead>
                  <TableHead>Executado por</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Observação</TableHead>
                  <TableHead>Resultado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((item) => {
                  const itemDate = item.created_at;
                  const success = isSuccess(item.status);
                  
                  return (
                    <TableRow key={item.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getTransactionTypeIcon(item.transaction_type)}
                          <span className="text-xs text-muted-foreground">
                            {getTransactionTypeLabel(item.transaction_type)}
                          </span>
                          {item.attempt_number && item.transaction_type === 'charge_attempt' && (
                            <Badge variant="outline" className="text-xs">
                              #{item.attempt_number}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {/* Data do pagamento - principal */}
                          <div className="font-medium">
                            {item.transaction_date 
                              ? safeFormat(new Date(item.transaction_date), 'dd/MM/yyyy', { locale: ptBR })
                              : '-'}
                          </div>
                          {/* Data de registro - secundária */}
                          {item.created_at && (
                            <div className="text-xs text-muted-foreground">
                              Reg: {safeFormat(new Date(item.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {formatPaymentMethod(item.payment_method)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {item.reference_number || '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {item.trigger_type === 'automatic' ? (
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800">
                            <RefreshCw className="h-3 w-3 mr-1" />
                            Automático
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800">
                            <User className="h-3 w-3 mr-1" />
                            {item.recorded_by_user?.display_name || item.recorded_by_user?.email || 'Manual'}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {success ? (
                          <Badge className="bg-green-500 text-white gap-1">
                            <CheckCircle2 className="h-3 w-3" />
                            Sucesso
                          </Badge>
                        ) : item.status === 'pending_confirmation' ? (
                          <Badge variant="outline" className="gap-1">
                            Pendente
                          </Badge>
                        ) : (
                          <Badge variant="destructive" className="gap-1">
                            <XCircle className="h-3 w-3" />
                            Falhou
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground max-w-[150px] truncate block">
                          {item.notes || '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {success ? (
                          <span className="text-sm text-green-600">
                            {item.stripe_payment_intent_id ? (
                              <code className="text-xs bg-muted px-1 py-0.5 rounded">
                                {item.stripe_payment_intent_id.substring(0, 15)}...
                              </code>
                            ) : item.amount ? (
                              `${item.currency || 'USD'} ${item.amount.toFixed(2)}`
                            ) : (
                              'OK'
                            )}
                          </span>
                        ) : (
                          <span className="text-sm text-destructive">
                            {getStripeErrorMessage(
                              item.error_code,
                              item.notes || 'Erro'
                            )}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </ScrollArea>

        <div className="flex justify-end pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
